import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";

const URL = "https://finendra.com/security";
const OG = "https://finendra.com/og/og-default.png";

const controls = [
  {
    h: "Encryption at Rest & in Transit",
    p: "All data is encrypted using AES-256 at rest and transmitted using TLS 1.3 encryption. Internal document portals and databases enforce encrypted storage volumes.",
  },
  {
    h: "Least-Privilege Role-Based Access",
    p: "Team members receive least-privilege access restricted specifically to client engagement scopes. Client data is strictly segregated — team members can only access clients in their direct portfolio pod.",
  },
  {
    h: "Zero Disbursement Authority (You Hold the Keys)",
    p: "Finendra operates strictly in preparer mode. We reconcile accounts, draft payroll runs, and stage AP bills in Bill.com or Ramp, but we never hold authorization to wire funds or release payments. Sole disbursement approval remains with you.",
  },
  {
    h: "Read-Only Banking Feeds",
    p: "We never ask for or store your primary root banking passwords. Bank feeds and statement syncs are configured using read-only accountant view permissions or direct native API integrations.",
  },
  {
    h: "Standard Mutual NDA & DPA",
    p: "Every engagement includes a comprehensive Non-Disclosure Agreement (NDA) and Data Protection Agreement (DPA) executed prior to onboarding, guaranteeing strict confidentiality and full client data ownership.",
  },
  {
    h: "Secure Multi-Factor Authentication (MFA)",
    p: "All Finendra operational accounts enforce hardware or authenticator app-based 2FA/MFA, single sign-on (SSO), and breach-monitored password hygiene.",
  },
  {
    h: "Delegated Accountant Invites",
    p: "Access to QuickBooks Online, Xero, Gusto, Stripe, and Rippling is provisioned through native delegated 'External Accountant' invitations under your company's own master billing account.",
  },
  {
    h: "Rapid Offboarding & Access Revocation",
    p: "Upon engagement conclusion or transition, our access is immediately revoked, cached session tokens invalidated, and written confirmation provided within 24 hours.",
  },
];

const responsibilities = [
  {
    h: "Finendra is responsible for",
    items: [
      "Enforcing AES-256 storage and TLS 1.3 transmission encryption",
      "Limiting internal access strictly to your assigned engagement pod",
      "Operating strictly in preparer mode without payment disbursement authority",
      "Executing standard NDA & DPA agreements prior to receiving financial data",
      "Maintaining audit logs of all access and entries made in client software",
    ],
  },
  {
    h: "You are responsible for",
    items: [
      "Granting delegated 'Accountant' or read-only access instead of sharing admin passwords",
      "Managing user permissions and dual-authorization payment approvals inside your banking portal",
      "Promptly revoking accountant access upon formal engagement offboarding",
      "Maintaining your own internal dual-control approval processes for disbursements",
    ],
  },
];

export const Route = createFileRoute("/security")({
  head: () => ({
    meta: [
      { title: "Security & Data Handling | Finendra" },
      {
        name: "description",
        content:
          "How Finendra controls access to client financial data, protects submissions on this site, and splits security responsibilities between our team and yours.",
      },
      { property: "og:title", content: "Security & Data Handling | Finendra" },
      {
        property: "og:description",
        content:
          "Access control, encryption in transit, confidentiality, and shared responsibilities for client financial data.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: SecurityPage,
});

function SecurityPage() {
  return (
    <>
      <PageHero
        eyebrow="Trust"
        title="How we handle your financial data."
        body="This page is maintained by Finendra to answer common security and privacy questions about how we work. It describes our current practices — it is not a certification or an independent audit."
      />

      <section className="px-6 pb-24">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-3">
          {controls.map((c, i) => (
            <Reveal
              key={c.h}
              delay={i * 0.05}
              className="rounded-3xl border border-brand-navy/10 bg-brand-surface p-8"
            >
              <h2 className="font-display text-lg font-bold text-brand-navy">{c.h}</h2>
              <p className="mt-3 leading-relaxed text-brand-navy/60">{c.p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-navy px-6 py-24">
        <div className="mx-auto grid max-w-5xl gap-12 md:grid-cols-2">
          {responsibilities.map((r, i) => (
            <Reveal key={r.h} delay={i * 0.08}>
              <h2 className="font-display text-2xl font-bold text-background">{r.h}</h2>
              <ul className="mt-6 space-y-4 text-background/65">
                {r.items.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 py-24">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-brand-navy">
            Reporting a security concern
          </h2>
          <p className="mt-5 leading-relaxed text-brand-navy/60">
            If you believe you have found a vulnerability in this site, or you have a question about
            how your data is handled, email{" "}
            <a
              href="mailto:connect@finendra.com"
              className="font-semibold text-brand-accent underline underline-offset-4"
            >
              connect@finendra.com
            </a>{" "}
            with the details. Please give us a reasonable window to investigate before disclosing
            anything publicly.
          </p>
          <p className="mt-5 text-brand-navy/60">
            For how we collect and retain information, see our{" "}
            <Link
              to="/privacy"
              className="font-semibold text-brand-accent underline underline-offset-4"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </Reveal>
      </section>

      <CtaPanel
        title="Have a security questionnaire to complete?"
        body="Send it over and we will work through it with you as part of the discovery process."
      />
    </>
  );
}
